import { useEffect, useState } from 'react'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import { useUser } from '../UserContext'
import { withOrg } from '../lib/withOrg'
import ConfirmModal from '../ConfirmModal'

const EMPTY_FORM = {
  project_id: '',
  name: '',
  right_type: '',
  quantity: 1,
  notes: ''
}

export default function Rights() {
  const toast = useToast()
  const { orgId } = useUser()
  const [rights, setRights] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [search, setSearch] = useState('')
  const [filterProject, setFilterProject] = useState('')
  const [form, setForm] = useState(EMPTY_FORM)

  useEffect(() => {
    fetchRights()
    fetchProjects()
  }, [])

  async function fetchRights() {
    setError(null)
    const { data, error: e } = await supabase
      .from('rights')
      .select('*, projects(name, deals(name, clients(name)))')
      .order('created_at', { ascending: false })
    if (e) { setError('Failed to load rights.'); setLoading(false); return }
    setRights(data || [])
    setLoading(false)
  }

  async function fetchProjects() {
    const { data } = await supabase
      .from('projects')
      .select('id, name, deals(name, clients(name))')
      .order('name')
    setProjects(data || [])
  }

  async function addRight() {
    if (!form.name.trim()) { toast.error('Right name is required'); return }
    if (!form.project_id) { toast.error('Please select a project'); return }
    const quantity = parseInt(form.quantity) || 1
    setSaving(true)

    const { data: rightData, error: insertError } = await supabase
      .from('rights')
      .insert([withOrg({ ...form, quantity }, orgId)])
      .select()

    if (insertError) { toast.error('Failed to save right.'); setSaving(false); return }

    const right = rightData[0]
    const deliveries = Array.from({ length: quantity }, (_, i) =>
      withOrg({
        right_id: right.id,
        status: 'pending',
        notes: quantity > 1 ? `Instance ${i + 1} of ${quantity}` : ''
      }, orgId)
    )

    const { error: delError } = await supabase.from('deliveries').insert(deliveries)
    setSaving(false)

    if (delError) {
      toast.error('Right saved but delivery records failed to create.')
    } else {
      toast.success(`Right added with ${quantity} delivery ${quantity === 1 ? 'record' : 'records'} created`)
    }

    setForm(EMPTY_FORM)
    setShowForm(false)
    fetchRights()
  }

  async function deleteRight(id) {
    const { error } = await supabase.from('rights').delete().eq('id', id)
    if (error) { toast.error('Failed to delete right.') } else { toast.success('Right deleted') }
    setConfirmDelete(null)
    fetchRights()
  }

  const filtered = rights.filter(r =>
    (r.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.right_type || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.projects?.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (r.projects?.deals?.name || '').toLowerCase().includes(search.toLowerCase())
  ).filter(r => filterProject ? r.project_id === filterProject : true)

  const rightToDelete = confirmDelete ? rights.find(r => r.id === confirmDelete) : null

  return (
    <div className="hv-page">
      <div className="hv-page-header">
        <div>
          <h1 className="hv-page-title">Rights</h1>
          <p className="hv-page-subtitle">Sponsorship rights assigned to each project</p>
        </div>
        <button className="hv-btn hv-btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : (
            <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Add Right</>
          )}
        </button>
      </div>

      {showForm && (
        <div className="hv-card" style={{ padding: '24px', marginBottom: '24px' }}>
          <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--hv-text-primary)', marginBottom: '20px' }}>New Right</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label className="hv-form-label">Project <span style={{ color: 'var(--hv-red)' }}>*</span></label>
              <select className="hv-select" value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })}>
                <option value=''>Select a project</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.deals?.name ? ` — ${p.deals.name}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="hv-form-label">Right Name <span style={{ color: 'var(--hv-red)' }}>*</span></label>
              <input className="hv-input" placeholder="e.g. Social Media Posts" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="hv-form-label">Right Type</label>
              <input className="hv-input" placeholder="e.g. Social, Branding, Event" value={form.right_type} onChange={e => setForm({ ...form, right_type: e.target.value })} />
            </div>
            <div>
              <label className="hv-form-label">Quantity</label>
              <input className="hv-input" type="number" min="1" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="hv-form-label">Notes</label>
              <input className="hv-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>

          <div style={{ background: 'var(--hv-accent-light)', borderRadius: 'var(--hv-radius-sm)', padding: '10px 14px', fontSize: '13px', color: 'var(--hv-accent)', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            This will create <strong>{parseInt(form.quantity) || 1}</strong> individual delivery {parseInt(form.quantity) === 1 ? 'record' : 'records'}.
          </div>

          <button className="hv-btn hv-btn-primary" onClick={addRight} disabled={saving}>
            {saving ? 'Saving...' : 'Save Right'}
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '20px' }}>
        <div className="hv-search-wrap" style={{ margin: 0 }}>
          <span className="hv-search-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </span>
          <input className="hv-search" type="text" placeholder="Search by right, type, project or deal..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="hv-select" value={filterProject} onChange={e => setFilterProject(e.target.value)}>
          <option value=''>All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {error && (
        <div style={{ background: 'var(--hv-red-bg)', border: '1px solid #fca5a5', borderRadius: 'var(--hv-radius)', padding: '14px 18px', color: 'var(--hv-red-text)', fontSize: '13.5px', marginBottom: '20px' }}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: 'var(--hv-text-muted)', fontSize: '13.5px', padding: '40px 0' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="hv-table-wrap">
          <div className="hv-empty">
            <div className="hv-empty-title">{search || filterProject ? 'No rights match your filters' : 'No rights yet'}</div>
            <div className="hv-empty-text">{search || filterProject ? 'Try adjusting your search.' : 'Add your first right above. Each right generates delivery records automatically.'}</div>
          </div>
        </div>
      ) : (
        <div className="hv-table-wrap">
          <div className="hv-table-count">{filtered.length} {filtered.length === 1 ? 'right' : 'rights'}{search || filterProject ? ' found' : ''}</div>
          <table className="hv-table">
            <thead>
              <tr>
                <th>Right Name</th>
                <th>Type</th>
                <th>Project</th>
                <th>Deal</th>
                <th>Client</th>
                <th>Deliveries</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(right => (
                <tr key={right.id}>
                  <td><span className="hv-table-name">{right.name}</span></td>
                  <td style={{ color: 'var(--hv-text-secondary)' }}>{right.right_type || '—'}</td>
                  <td style={{ color: 'var(--hv-text-secondary)' }}>{right.projects?.name || '—'}</td>
                  <td style={{ color: 'var(--hv-text-secondary)' }}>{right.projects?.deals?.name || '—'}</td>
                  <td style={{ color: 'var(--hv-text-secondary)' }}>{right.projects?.deals?.clients?.name || '—'}</td>
                  <td><span className="hv-badge hv-badge-gray">{right.quantity || 1}</span></td>
                  <td>
                    <div className="hv-table-actions">
                      <button className="hv-btn hv-btn-danger" style={{ fontSize: '13px' }} onClick={() => setConfirmDelete(right.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete right"
          message={`"${rightToDelete?.name}" and all its delivery records will be permanently removed.`}
          confirmLabel="Delete Right"
          onConfirm={() => deleteRight(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  )
}